import { useCallback, useEffect, useRef, useState } from "react";
import getSpeechmaticsRealtimeToken, { formatTranscriptionError } from "../api/speechmatics";
import useOkhatiAiSettings from "./useOkhatiAiSettings";

const SAMPLE_RATE = 16_000;
const WORKLET_URL = "/js/pcm-audio-worklet.min.js";

export type SpeechToTextStatus = "idle" | "listening" | "error";

interface UseSpeechToTextOptions {
  onTranscript: (text: string, isFinal: boolean) => void;
  language?: string;
}

interface UseSpeechToTextResult {
  status: SpeechToTextStatus;
  error: string | null;
  start: () => Promise<boolean>;
  stop: () => void;
  pause: () => void;
  resume: () => void;
  getTranscript: () => string;
  isActive: boolean;
  analyser: AnalyserNode | null;
}

type SpeechmaticsClient = {
  addEventListener: (
    event: string,
    handler: (payload: { data: Record<string, unknown> }) => void
  ) => void;
  start: (jwt: string, config: Record<string, unknown>) => Promise<void>;
  sendAudio: (data: ArrayBuffer) => void;
  stopRecognition: (options?: { noTimeout?: boolean }) => Promise<void>;
};

type SpeechmaticsRecorder = {
  addEventListener: (event: string, handler: (event: { data: ArrayBuffer }) => void) => void;
  startRecording: (options: { audioContext: AudioContext }) => Promise<void>;
  stopRecording: () => void;
  mute: () => void;
  unmute: () => void;
  analyser?: AnalyserNode | null;
};

export default function useSpeechToText({
  onTranscript,
  language = "en"
}: UseSpeechToTextOptions): UseSpeechToTextResult {
  const { transcriptionApiKey } = useOkhatiAiSettings();

  const [status, setStatus] = useState<SpeechToTextStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);

  const clientRef = useRef<SpeechmaticsClient | null>(null);
  const recorderRef = useRef<SpeechmaticsRecorder | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const isStartingRef = useRef(false);
  const finalTranscriptRef = useRef("");
  const partialTranscriptRef = useRef("");
  const onTranscriptRef = useRef(onTranscript);
  const isPausedRef = useRef(false);
  const partialThrottleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastPartialTextRef = useRef("");

  const emitPartialTranscript = useCallback((text: string) => {
    lastPartialTextRef.current = text;
    if (partialThrottleRef.current) {
      clearTimeout(partialThrottleRef.current);
    }
    partialThrottleRef.current = setTimeout(() => {
      partialThrottleRef.current = null;
      try {
        onTranscriptRef.current(lastPartialTextRef.current, false);
      } catch {
        // Parent update errors must not crash transcription.
      }
    }, 150);
  }, []);

  useEffect(() => {
    onTranscriptRef.current = onTranscript;
  }, [onTranscript]);

  const cleanup = useCallback(() => {
    if (partialThrottleRef.current) {
      clearTimeout(partialThrottleRef.current);
      partialThrottleRef.current = null;
    }

    try {
      recorderRef.current?.stopRecording();
    } catch {
      // Recorder may already be stopped.
    }
    recorderRef.current = null;
    setAnalyser(null);

    clientRef.current?.stopRecognition({ noTimeout: true }).catch(() => undefined);
    clientRef.current = null;

    if (audioContextRef.current?.state !== "closed") {
      audioContextRef.current?.close().catch(() => undefined);
    }
    audioContextRef.current = null;

    finalTranscriptRef.current = "";
    partialTranscriptRef.current = "";
    lastPartialTextRef.current = "";
    isPausedRef.current = false;
  }, []);

  const getTranscript = useCallback((): string => {
    if (partialThrottleRef.current) {
      clearTimeout(partialThrottleRef.current);
      partialThrottleRef.current = null;
    }

    const partial = partialTranscriptRef.current;
    const combined = partial
      ? [finalTranscriptRef.current, partial].filter(Boolean).join(" ")
      : finalTranscriptRef.current || lastPartialTextRef.current;

    return combined.trim();
  }, []);

  const stop = useCallback(() => {
    cleanup();
    setStatus("idle");
  }, [cleanup]);

  const pause = useCallback(() => {
    if (status !== "listening") return;
    isPausedRef.current = true;
    partialTranscriptRef.current = "";
    recorderRef.current?.mute();
    try {
      onTranscriptRef.current(finalTranscriptRef.current, true);
    } catch {
      // Ignore parent update errors.
    }
  }, [status]);

  const resume = useCallback(() => {
    if (status !== "listening") return;
    isPausedRef.current = false;
    recorderRef.current?.unmute();
  }, [status]);

  const start = useCallback(async (): Promise<boolean> => {
    if (status === "listening" || isStartingRef.current) return false;

    isStartingRef.current = true;
    setError(null);
    finalTranscriptRef.current = "";
    partialTranscriptRef.current = "";

    try {
      if (!transcriptionApiKey) {
        throw new Error("Transcription API key is not configured");
      }

      const [{ PCMRecorder }, { RealtimeClient }] = await Promise.all([
        import("@speechmatics/browser-audio-input"),
        import("@speechmatics/real-time-client")
      ]);

      const jwt = await getSpeechmaticsRealtimeToken({
        apiKey: transcriptionApiKey
      });
      const client = new RealtimeClient({ appId: "okhatiui" }) as unknown as SpeechmaticsClient;
      const recorder = new PCMRecorder(WORKLET_URL) as unknown as SpeechmaticsRecorder;
      const audioContext = new AudioContext({ sampleRate: SAMPLE_RATE });

      clientRef.current = client;
      recorderRef.current = recorder;
      audioContextRef.current = audioContext;

      client.addEventListener("receiveMessage", ({ data }) => {
        try {
          if (isPausedRef.current) return;

          if (data.message === "AddPartialTranscript") {
            const metadata = data.metadata as { transcript?: string } | undefined;
            const partial = metadata?.transcript;
            if (partial) {
              partialTranscriptRef.current = partial;
              const combined = [finalTranscriptRef.current, partial].filter(Boolean).join(" ");
              emitPartialTranscript(combined);
            }
          } else if (data.message === "AddTranscript") {
            const metadata = data.metadata as { transcript?: string } | undefined;
            const finalText = metadata?.transcript;
            if (finalText) {
              finalTranscriptRef.current = finalTranscriptRef.current
                ? `${finalTranscriptRef.current} ${finalText}`
                : finalText;
              partialTranscriptRef.current = "";
              onTranscriptRef.current(finalTranscriptRef.current, true);
            }
          } else if (data.message === "Error") {
            setError((data.reason as string) || "Speechmatics transcription error");
            setStatus("error");
            cleanup();
          }
        } catch {
          setError("Speechmatics transcription error");
          setStatus("error");
          cleanup();
        }
      });

      recorder.addEventListener("audio", (event) => {
        if (isPausedRef.current) return;

        try {
          client.sendAudio(event.data);
        } catch {
          // Socket may close before recorder stops; safe to ignore.
        }
      });

      await client.start(jwt, {
        transcription_config: {
          language,
          enable_partials: true,
          max_delay: 0.7
        },
        audio_format: {
          type: "raw",
          encoding: "pcm_f32le",
          sample_rate: SAMPLE_RATE
        }
      });

      await recorder.startRecording({ audioContext });
      setAnalyser(recorder.analyser ?? null);
      setStatus("listening");
      return true;
    } catch (err) {
      cleanup();
      setError(formatTranscriptionError(err));
      setStatus("error");
      return false;
    } finally {
      isStartingRef.current = false;
    }
  }, [cleanup, emitPartialTranscript, language, status, transcriptionApiKey]);

  useEffect(() => () => cleanup(), [cleanup]);

  return {
    status,
    error,
    start,
    stop,
    pause,
    resume,
    getTranscript,
    isActive: status === "listening",
    analyser
  };
}
